import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { AlarmAgentApiClient } from './api-client.service';
import jwt_decode from 'jwt-decode';
import { UserRole } from '../enums/user-role.enum';
import Cohere from 'cohere-js';
import { ConfigService } from './config.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private _authKey = 'AAJWT';

	constructor(
		private cookieService: CookieService,
		private http: AlarmAgentApiClient,
		private config: ConfigService
	) {}

	async login(username: string, password: string): Promise<boolean> {
		try {
			const result: any = await this.http
				.post(
					'access-token',
					{ Username: username, Password: password },
					{ responseType: 'text' }
				)
				.toPromise();

			this.setToken(result, username);

			return true;
		} catch (err) {
			throw err;
		}
	}

	setToken(tokenString: string, username: string) {
		if (!tokenString) {
			return;
		}

		Cohere.identify(
			tokenString, // Required: can be any unique ID
			{
				displayName: username,
			}
		);

		this.storeAuthData(tokenString);
	}

	isUserAuthenticated(): boolean {
		try {
			let jwt = this.getAuthData();

			// If token is expired, remove it
			if (new Date() >= jwt.expiration) {
				this.clearAuthData();
				return false;
			}

			return true;
		} catch {
			this.clearAuthData();
			return false;
		}
	}

	clearAuthData() {
		this.cookieService.delete(this._authKey);
		this.cookieService.deleteAll('/');
		this.cookieService.deleteAll('/', this.config.webBaseUrl);
		this.cookieService.deleteAll('/', `${this.config.webBaseUrl}/webapp`);
	}

	getAuthString(): string {
		return this.cookieService.get(this._authKey);
	}

	getAuthData(): JWT {
		return new JWT(jwt_decode(this.getAuthString()));
	}

	isUserRole(role: UserRole): boolean {
		return (
			this.isUserAuthenticated() &&
			this.getAuthData().roles.includes(role)
		);
	}

	private storeAuthData(authToken: string) {
		this.cookieService.set(this._authKey, authToken);
	}
}

export class JWT {
	constructor(value: any) {
		if (!value) {
			throw new Error('no value was provided to jwt initializer');
		}

		this.userId = parseInt(value.sub);
		this.companyId = parseInt(value.cId);
		this.issuer = value.iss;
		this.roles = value.roles;
		this.userName = value.userName;
		this.email = value.email;

		// javascript dates use milliseconds, but jwt uses seconds
		this.issued = new Date(value.iat * 1000);
		this.notValidBefore = new Date(value.nbf * 1000);
		this.expiration = new Date(value.exp * 1000);
	}

	userId: number;
	companyId: number;
	issued: Date;
	notValidBefore: Date;
	expiration: Date;
	issuer: string;
	roles: string[];
	userName: string;
	email: string;
}
